/**
 * Client-side send readiness. The server enforces the same rules; these keep
 * the button honest before the round trip. No '@/utils' import on purpose.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const parseEmails = (value = '') =>
  String(value).split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

export const requiresTracking = (via = []) => via.includes('certified_mail');

export const emailRecipientsValid = (via = [], to = []) =>
  !via.includes('email') || (to.length > 0 && to.every((e) => EMAIL_RE.test(e)));

/**
 * Whether the letter itself is in a sendable state: nothing left to fill in,
 * no missing merge values, and the status the firm's approval rule expects.
 */
export const canSend = ({ status, fillIns = [], missing = {}, approvalRequired = false }) => {
  if (fillIns.length > 0) return false;
  if (Object.keys(missing || {}).length > 0) return false;
  if (approvalRequired) return status === 'approved';
  return status === 'draft' || status === 'approved';
};

/** Human-readable reasons the send button is disabled, in display order. */
export const sendBlockers = ({ status, fillIns = [], missing = {}, approvalRequired = false, via = [], to = [], tracking = '' }) => {
  const reasons = [];
  if (fillIns.length > 0) reasons.push(`${fillIns.length} fill-in prompt${fillIns.length === 1 ? '' : 's'} still to write`);
  const missingLabels = Object.values(missing || {});
  if (missingLabels.length > 0) reasons.push(`Missing matter values: ${missingLabels.join(', ')}`);
  if (approvalRequired && status !== 'approved') {
    reasons.push(status === 'pending_approval' ? 'Waiting for approval' : 'Approval is required before sending');
  }
  if (!approvalRequired && !['draft', 'approved'].includes(status)) reasons.push('Only a draft can be sent');
  if (via.length === 0) reasons.push('Choose at least one delivery method');
  if (!emailRecipientsValid(via, to)) reasons.push('Enter a valid recipient email address');
  if (requiresTracking(via) && !String(tracking || '').trim()) reasons.push('Certified mail needs a tracking number');
  return reasons;
};
